<template>
    <div class="relative w-full rounded-t-lg">
        <div class="relative h-72 w-full md:h-80">
            <div
                :class="[
                    'absolute inset-0 overflow-hidden rounded-t-lg',
                    hasCoverPhoto ? 'bg-cover bg-center' : 'flex items-center justify-center bg-rent-risk-blue-100',
                ]"
                :style="hasCoverPhoto ? { backgroundImage: `url('${coverDisplaySrc}')` } : null"
            >
                <img
                    v-if="!hasCoverPhoto"
                    :src="placeholderSrc"
                    :alt="property.full_address"
                    class="max-h-full max-w-full object-contain p-6 opacity-90"
                />

                <div
                    v-if="hasCoverPhoto"
                    class="pointer-events-none absolute inset-0 bg-gradient-to-t from-gray-900/70 via-gray-900/30 to-transparent"
                    aria-hidden="true"
                />
            </div>

            <div class="absolute left-3 top-3 z-30 flex items-center gap-2">
                <button
                    v-if="!property.deleted_at && !isSharedViewOnly"
                    type="button"
                    class="inline-flex items-center gap-2 rounded-md bg-white/90 px-3 py-2 text-sm font-semibold text-gray-800 shadow ring-1 ring-gray-200 backdrop-blur hover:bg-white"
                    @click="showPhotosManager = true"
                >
                    <font-awesome-icon :icon="faImages" class="h-4 w-4" aria-hidden="true" />
                    {{ hasGalleryPhotos ? 'Add More Photos' : 'Add Photos' }}
                </button>
                <slot name="badge" />
            </div>

            <div class="absolute right-3 top-3 z-30 flex flex-wrap items-center justify-end gap-2">
                <slot name="actions" />
            </div>

            <div
                v-if="hasCoverPhoto"
                class="pointer-events-none absolute inset-x-0 bottom-0 z-10 px-4 pb-4 pt-12 sm:px-6 sm:pb-6"
            >
                <slot name="details" />
            </div>
        </div>

        <PropertyPhotosManagerModal
            v-if="!property.deleted_at && !isSharedViewOnly"
            :show="showPhotosManager"
            :property="property"
            @close="showPhotosManager = false"
        />
    </div>
</template>

<script setup>
import { appUrl } from '@/appUrl'
import { computed, ref } from 'vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faImages } from '@fortawesome/pro-solid-svg-icons'
import PropertyPhotosManagerModal from '../PropertyPhotosManagerModal.vue'

const props = defineProps({
    property: {
        type: Object,
        required: true,
    },
    propertyAccess: {
        type: String,
        default: 'owner',
    },
})

const placeholderSrc = '/default-property-image.jpg'

const showPhotosManager = ref(false)

const coverDisplaySrc = computed(() => {
    if (!props.property.photo) {
        return null
    }

    return props.property.photo.startsWith('http')
        ? props.property.photo
        : appUrl(`/storage/${props.property.photo}`)
})

const hasCoverPhoto = computed(() => Boolean(coverDisplaySrc.value))
const hasGalleryPhotos = computed(() => (props.property.photos || []).length > 0)
const isSharedViewOnly = computed(() => props.propertyAccess === 'shared_view')
</script>
